import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';

interface NavalEncounterGeneratorProps {
  weatherData: {
    climate: string;
    temperature: string;
    wind: string;
    windStrength: string;
    windDirection: string;
    precipitation: string;
  };
  crewMorale: string;
  navigationState: string;
  shipCleanliness: string;
}

const API_ENDPOINT = 'https://kbydfuxse7.execute-api.us-east-1.amazonaws.com/prod/generate-encounter';

const NavalEncounterGenerator: React.FC<NavalEncounterGeneratorProps> = ({
  weatherData,
  crewMorale,
  navigationState,
  shipCleanliness
}) => {
  const [additionalContext, setAdditionalContext] = useState('');
  const [encounter, setEncounter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateEncounter = async () => {
    setIsLoading(true);
    setError(null);
    
    const prompt = `Generate a naval encounter for a D&D game based on the following context:
    Climate: ${weatherData.climate}
    Temperature: ${weatherData.temperature}
    Wind: ${weatherData.wind}
    Wind Strength: ${weatherData.windStrength}
    Wind Direction: ${weatherData.windDirection}
    Precipitation: ${weatherData.precipitation}
    Crew Morale: ${crewMorale}
    Navigation State: ${navigationState}
    Ship Cleanliness: ${shipCleanliness}
    Additional Context: ${additionalContext}

    Please provide a brief description of the encounter, including any potential challenges, enemies, or discoveries the players might face.`;

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `API error: ${response.status} ${response.statusText}`
        );
      }
  
      const data = await response.json();
      if (data.generatedContent) {
        setEncounter(data.generatedContent);
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      setEncounter('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto mt-4">
      <CardHeader>
        <CardTitle>Naval Encounter Generator</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Textarea
            placeholder="Additional context (optional)"
            value={additionalContext}
            onChange={(e) => setAdditionalContext(e.target.value)}
          />

          <Button 
            onClick={generateEncounter} 
            className="w-full" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate Encounter'
            )}
          </Button>

          {error && (
            <div className="mt-4 p-4 bg-red-100 dark:bg-red-900 rounded-md text-red-900 dark:text-red-100">
              <h4 className="font-semibold mb-2">Error:</h4>
              <p>{error}</p>
            </div>
          )}

          {encounter && (
            <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-md text-gray-900 dark:text-gray-100">
              <h4 className="font-semibold mb-2">Generated Encounter:</h4>
              <p>{encounter}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default NavalEncounterGenerator;